import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { addRendition } from '@shared/utils/photoRendition';
import { HIGH_RES_RENDITION, LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/renditions';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { getHeaderPhotoUrls } from './getHeaderPhotoUrls';

type SmartPreloadResult = Readonly<{
  criticalUrls: ReadonlyArray<string>;
  deferredUrls: ReadonlyArray<string>;
}>;

// Critical (current route) vs deferred (idle-callback) image set.
// Welcome route: header photos. Specific page: that page's photo.
// Honeymoon-redesign uses xlarge for the current page (LCP candidate, no crossfade).
//
// PRNT-4849 checked this file for the srcSet/sizes-vs-preload coupling it introduced on
// OptimizedEventGuestSitePhoto's fixed-aspect/square <img> branches: a <link rel="preload">
// that fetches one fixed URL while the real <img> resolves a *different* srcSet candidate would
// silently waste the preload (double fetch, no LCP benefit). That risk doesn't apply here today —
// every critical/deferred URL this hook returns is built at LOW_RES_RENDITION ('tiny', the
// crossfade's blur placeholder, which stays a single fixed-size image on purpose) EXCEPT the
// honeymoon-redesign branch below, which uses HIGH_RES_RENDITION for a hero rendered by a
// different component (SimpleModeHero, not OptimizedEventGuestSitePhoto) that this PR didn't
// touch. If a future change routes a `criticalUrls`/`deferredUrls` entry through
// OptimizedEventGuestSitePhoto's responsive high-res layer, give it matching
// `imagesrcset`/`imagesizes` on the `<link>` in PreloadImages.tsx — see
// useProgressiveImage.ts's `containerWidthPx` doc for why the two must stay in lockstep.
export function useSmartPreloadImageUrls(
  pages: ReadonlyArray<EventPageFragment>,
  eventHandle: string,
  headerPhoto: SupportedHeaderPhotoLayoutSchema | null | undefined,
  fallbackPhotoUrl?: string | null,
): SmartPreloadResult {
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  // checkIsWelcomePage is stable — useWelcomePage wraps it in useCallback
  const { checkIsWelcomePage } = useWelcomePage();
  const isStaysRedesignActive = useIsStaysRedesignActive();

  return useMemo(() => {
    const { allUrls: headerUrls, criticalCount: headerCriticalCount } = getHeaderPhotoUrls(headerPhoto);

    // Deferred-set URLs always at LOW_RES_RENDITION (do not swap to xlarge — bandwidth regression).
    const pagePhotoUrlsBySlug = new Map<string, string>();
    for (const page of pages) {
      if (page.photoV2?.url) {
        const url = addRendition({ url: page.photoV2.url, renditionSize: LOW_RES_RENDITION });
        pagePhotoUrlsBySlug.set(page.pageSlug, url);
      }
    }

    // Determine if user is on the welcome page
    const currentPage = currentPageSlug ? pages.find(p => p.pageSlug === currentPageSlug) : undefined;
    const isOnWelcomePage = !currentPageSlug || (currentPage ? checkIsWelcomePage(currentPage.pageSlug, currentPage.type).isWelcomePage : true);

    let criticalUrls: string[];
    let deferredUrls: string[];

    if (isOnWelcomePage) {
      // Welcome route: preload header photos. Welcome hero stays at tiny (crossfade preserved).
      criticalUrls = headerUrls.slice(0, headerCriticalCount);

      // Fallback for events with no active header (legacy Aloha or no header configured).
      if (criticalUrls.length === 0) {
        const welcomePage = pages.find(p => checkIsWelcomePage(p.pageSlug, p.type).isWelcomePage);
        const welcomePhotoUrl = welcomePage?.photoV2?.url ?? fallbackPhotoUrl ?? null;
        if (welcomePhotoUrl) {
          criticalUrls = [addRendition({ url: welcomePhotoUrl, renditionSize: LOW_RES_RENDITION })];
        }
      }

      const remainingHeaderUrls = headerUrls.slice(headerCriticalCount);
      deferredUrls = [...remainingHeaderUrls, ...Array.from(pagePhotoUrlsBySlug.values())];
    } else {
      // Specific page route. Honeymoon-redesign uses xlarge (LCP); else tiny.
      const currentPageRendition = isStaysRedesignActive ? HIGH_RES_RENDITION : LOW_RES_RENDITION;
      const currentPagePhotoUrl = currentPage?.photoV2?.url ? addRendition({ url: currentPage.photoV2.url, renditionSize: currentPageRendition }) : undefined;
      const fallbackUrl = fallbackPhotoUrl ? addRendition({ url: fallbackPhotoUrl, renditionSize: currentPageRendition }) : undefined;
      const photoToPreload = currentPagePhotoUrl || fallbackUrl;
      criticalUrls = photoToPreload ? [photoToPreload] : [];

      // Slug-based exclusion (not URL-string) so renditions don't collide for the same page.
      const otherPagePhotoUrls = currentPageSlug
        ? Array.from(pagePhotoUrlsBySlug.entries())
            .filter(([slug]) => slug !== currentPageSlug)
            .map(([, url]) => url)
        : Array.from(pagePhotoUrlsBySlug.values());
      deferredUrls = [...headerUrls, ...otherPagePhotoUrls];
    }

    // Deduplicate: remove from deferred anything already in critical
    const criticalSet = new Set(criticalUrls);
    deferredUrls = deferredUrls.filter(url => !criticalSet.has(url));

    return { criticalUrls, deferredUrls };
  }, [pages, currentPageSlug, headerPhoto, checkIsWelcomePage, fallbackPhotoUrl, isStaysRedesignActive]);
}
